import {
  DYNAMIC_CHARTING_TX_PLAN_DEMO,
  DYNAMIC_CHARTING_TX_PLAN_DEMO_PROCEDURES,
  DYNAMIC_CHARTING_TUTORIAL_PATIENT,
} from "@/tutorials/dynamicChartingDemo";

export const PATIENT_OVERVIEW_DEMO_ID = "tutorial-patient-overview";
export const PATIENT_OVERVIEW_DEMO_ROUTE = `/patients/${PATIENT_OVERVIEW_DEMO_ID}?tutorial=patient-overview&demo=1`;

const VISIT_DATE_INITIAL = "2026-05-18T09:00:00.000Z";
const VISIT_DATE_TX_PLAN = "2026-05-25T10:00:00.000Z";

const DEMO_FINDINGS = flattenFindingCatalog(DYNAMIC_CHARTING_TX_PLAN_DEMO.paletteCategories);

const TX_PLAN_PHASE_NAMES = {
  1: "Visit 1 · RCT start",
  2: "Visit 2 · finish RCT",
  3: "Visit 3 · Zirconia prep",
  4: "Visit 4 · Zirconia try-in",
  5: "Visit 5 · Zirconia cementation",
};

const TX_PLAN_ITEMS = buildTxPlanItems();

export const PATIENT_OVERVIEW_DEMO = {
  patient: {
    ...DYNAMIC_CHARTING_TUTORIAL_PATIENT,
    id: PATIENT_OVERVIEW_DEMO_ID,
    full_name: "Juan Dela Cruz",
    first_name: "Juan",
    last_name: "Dela Cruz",
    status: "Active",
    phone: "09171234567",
    email: "juan.delacruz@example.com",
    messenger_conversation_url: "https://business.facebook.com/latest/inbox/all/juan-demo",
    emergency_contact_name: "Ana Dela Cruz",
    emergency_contact_phone: "09179876543",
    address: "Quezon City, Metro Manila",
    patient_number: "DEMO-0001",
    preferred_contact_method: "sms",
    preferred_doctor_name: "Dr. Levi Jordan",
    has_medical_history: true,
    recall_status: "Priority",
    recall_opt_in: true,
    senior_pwd_status: "none",
    last_visit_date: "2026-05-25",
    current_phase: 2,
    total_phases: 4,
    pending_treatment_centavos: 1960000,
    balance_centavos: 0,
  },
  ltvSummary: {
    currency: "PHP",
    lifetime_collected_centavos: 2860000,
    confirmed_payment_count: 2,
    last_payment_date: VISIT_DATE_TX_PLAN,
  },
  txPlanAlerts: [
    {
      plan_id: "tutorial-patient-overview-tx-plan",
      status: "Accepted",
      pipeline_state: "booked",
      total_centavos: 2650000,
    },
  ],
  charting: {
    ...DYNAMIC_CHARTING_TX_PLAN_DEMO,
    draftId: "patient-overview-chart-demo",
    findings: DEMO_FINDINGS,
    snapshot: {
      id: "patient-overview-current-chart",
      state: DYNAMIC_CHARTING_TX_PLAN_DEMO.state,
      finalized_at: VISIT_DATE_INITIAL,
      finalized_by_name: "Dr. Levi Jordan",
    },
    history: [
      {
        entry_id: "history-current-chart",
        entry_type: "current_chart",
        layer_name: "Current chart",
        occurred_at: VISIT_DATE_INITIAL,
        finalized_at: VISIT_DATE_INITIAL,
        finalized_by_name: "Dr. Levi Jordan",
      },
      {
        entry_id: "history-completed-work",
        entry_type: "completed_work",
        layer_name: "Completed work",
        occurred_at: VISIT_DATE_INITIAL,
        doctor_name: "Dr. Levi Jordan",
        procedures: [
          completedWork("Composite Restoration", ["15"], ["O"], 250000),
          completedWork("Composite Restoration", ["16"], ["O"], 250000),
          completedWork("Composite Restoration", ["24"], ["D"], 250000),
          completedWork("Composite Restoration", ["27"], ["O"], 250000),
          completedWork("Composite Restoration", ["36"], ["O"], 250000),
          completedWork("Composite Restoration", ["37"], ["D"], 250000),
          completedWork("Composite Restoration", ["44"], ["O"], 250000),
          completedWork("Tooth Extraction", ["48"], [], 300000),
          completedWork("Oral Prophylaxis", [], [], 120000),
        ],
      },
    ],
  },
  overviewChart: {
    snapshot: {
      id: "patient-overview-current-chart",
      state: DYNAMIC_CHARTING_TX_PLAN_DEMO.state,
      finalized_at: VISIT_DATE_INITIAL,
      finalized_by_name: "Dr. Levi Jordan",
    },
    findings: DEMO_FINDINGS,
    planItems: TX_PLAN_ITEMS,
  },
  treatment: {
    plans: [
      {
        id: "tutorial-patient-overview-tx-plan",
        plan_code: "TP-TUTORIAL",
        status: "Accepted",
        created_at: "2026-05-18T14:00:00.000Z",
        item_count: TX_PLAN_ITEMS.length,
      },
    ],
    activePlanId: "tutorial-patient-overview-tx-plan",
    activePlan: {
      id: "tutorial-patient-overview-tx-plan",
      plan_code: "TP-TUTORIAL",
      status: "Accepted",
      notes: "Accepted RCT plus Zirconia case from Dynamic Charting.",
      items: TX_PLAN_ITEMS,
      phase_names: TX_PLAN_PHASE_NAMES,
      phase_meta: {},
      tooth_notes: {
        46: "Patient reports intermittent pain. Prioritize RCT sequence before crown.",
      },
      doctor_name: "Dr. Levi Jordan",
      doctor_prc_license: "PRC-0000000",
      created_at: "2026-05-18T14:00:00.000Z",
    },
    procedures: DYNAMIC_CHARTING_TX_PLAN_DEMO_PROCEDURES,
    findings: DEMO_FINDINGS,
    paletteCategories: DYNAMIC_CHARTING_TX_PLAN_DEMO.paletteCategories,
    snapshot: { state: DYNAMIC_CHARTING_TX_PLAN_DEMO.state },
    chairSetupMinutes: 15,
    medicalHistory: { status: "Verified", data: medicalHistoryData() },
    planBalance: {
      plan_total_centavos: 2650000,
      paid_centavos: 690000,
      remaining_centavos: 1960000,
    },
  },
  ortho: {
    status: "Screened, no active ortho case",
    note: "Mild lower anterior crowding noted during the first visit. No orthodontic case was opened because the patient is prioritizing RCT plus Zirconia.",
    checkedBy: "Dr. Levi Jordan",
    checkedAt: VISIT_DATE_INITIAL,
  },
  visits: [
    {
      id: "demo-visit-initial",
      finalized_at: VISIT_DATE_INITIAL,
      created_at: VISIT_DATE_INITIAL,
      status: "Finalized",
      doctor_name: "Dr. Levi Jordan",
      procedure_count: 9,
      notes: "Initial charting, same-day restorations, extraction, and hygiene.",
      charges_centavos: 2170000,
      payments_centavos: 2170000,
      balance_centavos: 0,
    },
    {
      id: "demo-visit-tx-plan-1",
      finalized_at: VISIT_DATE_TX_PLAN,
      created_at: VISIT_DATE_TX_PLAN,
      status: "Finalized",
      doctor_name: "Dr. Levi Jordan",
      procedure_count: 1,
      notes: "Tx Plan Visit 1: RCT start on tooth 46. Next visit already booked.",
      charges_centavos: 690000,
      payments_centavos: 690000,
      balance_centavos: 0,
    },
  ],
  visitDetailsById: {
    "demo-visit-initial": {
      id: "demo-visit-initial",
      finalized_at: VISIT_DATE_INITIAL,
      created_at: VISIT_DATE_INITIAL,
      status: "Finalized",
      doctor_name: "Dr. Levi Jordan",
      notes: "Initial charting, same-day restorations, extraction, and hygiene.",
      charges_centavos: 2170000,
      payments_centavos: 2170000,
      balance_centavos: 0,
      events: [
        charge("visit1-c1", "Composite Restoration · Tooth 15 O", 250000),
        charge("visit1-c2", "Composite Restoration · Tooth 16 O", 250000),
        charge("visit1-c3", "Composite Restoration · Tooth 24 D", 250000),
        charge("visit1-c4", "Composite Restoration · Tooth 27 O", 250000),
        charge("visit1-c5", "Composite Restoration · Tooth 36 O", 250000),
        charge("visit1-c6", "Composite Restoration · Tooth 37 D", 250000),
        charge("visit1-c7", "Composite Restoration · Tooth 44 O", 250000),
        charge("visit1-ext", "Tooth Extraction · Tooth 48", 300000),
        charge("visit1-prophy", "Oral Prophylaxis", 120000),
        payment("visit1-pay", "GCash", 2170000, "GC-482913"),
      ],
    },
    "demo-visit-tx-plan-1": {
      id: "demo-visit-tx-plan-1",
      finalized_at: VISIT_DATE_TX_PLAN,
      created_at: VISIT_DATE_TX_PLAN,
      status: "Finalized",
      doctor_name: "Dr. Levi Jordan",
      notes: "Tx Plan Visit 1: RCT start on tooth 46. Next visit already booked.",
      charges_centavos: 690000,
      payments_centavos: 690000,
      balance_centavos: 0,
      events: [
        charge("visit2-rct", "Tx Plan installment · Visit 1", 690000),
        payment("visit2-pay", "Cash", 690000, "OR-000284"),
      ],
    },
  },
  payments: [
    {
      id: "payment-gcash-initial",
      date: VISIT_DATE_INITIAL,
      method: "GCash",
      status: "Confirmed",
      amount_centavos: 2170000,
      reference: "GC-482913",
      appliesTo: "Initial completed work",
    },
    {
      id: "payment-cash-tx-plan",
      date: VISIT_DATE_TX_PLAN,
      method: "Cash",
      status: "Confirmed",
      amount_centavos: 690000,
      reference: "OR-000284",
      appliesTo: "Tx Plan Visit 1 installment",
    },
  ],
  files: [
    {
      id: "file-pano",
      name: "Panoramic x-ray",
      type: "X-ray",
      date: VISIT_DATE_INITIAL,
      owner: "Dr. Levi Jordan",
      note: "Used to confirm tooth 46 endodontic plan and tooth 48 extraction.",
    },
    {
      id: "file-tx-proposal",
      name: "Accepted treatment proposal",
      type: "Tx Plan PDF",
      date: "2026-05-18T14:30:00.000Z",
      owner: "Front Desk",
      note: "Patient accepted phased treatment before checkout.",
    },
    {
      id: "file-med-history",
      name: "Verified medical history",
      type: "Intake form",
      date: "2026-05-18T08:45:00.000Z",
      owner: "Dr. Levi Jordan",
      note: "Hypertension controlled. No known drug allergies.",
    },
    {
      id: "file-payment-proof",
      name: "GCash payment proof",
      type: "Payment proof",
      date: VISIT_DATE_INITIAL,
      owner: "Front Desk",
      note: "Reference GC-482913.",
    },
  ],
  medicalHistory: {
    status: "Verified",
    history: {
      id: "demo-medical-history",
      status: "Verified",
      is_current: true,
      data: medicalHistoryData(),
      submitted_at: "2026-05-18T08:40:00.000Z",
      verified_at: "2026-05-18T08:50:00.000Z",
      verifier_name: "Dr. Levi Jordan",
      expires_at: "2027-05-18T00:00:00.000Z",
    },
    visitVitals: {
      bp_systolic: 128,
      bp_diastolic: 82,
      pulse_bpm: 78,
      measured_at: VISIT_DATE_TX_PLAN,
    },
  },
  history: [
    historyRow("history-created", "patient.created", "Patient chart created for Juan Dela Cruz.", "Front Desk", "2026-05-18T08:30:00.000Z"),
    historyRow("history-medical", "medical_history.verified", "Medical history verified before treatment.", "Dr. Levi Jordan", "2026-05-18T08:50:00.000Z"),
    historyRow("history-chart", "chart.finalized", "Initial charting finalized with restorative, endodontic, hygiene, and extraction findings.", "Dr. Levi Jordan", VISIT_DATE_INITIAL),
    historyRow("history-today", "checkout.completed", "Completed initial visit checkout with GCash payment.", "Front Desk", "2026-05-18T11:30:00.000Z"),
    historyRow("history-plan", "tx_plan.accepted", "Tx Plan accepted and phased into five appointments.", "Dr. Levi Jordan", "2026-05-18T14:30:00.000Z"),
    historyRow("history-visit", "visit.finalized", "Tx Plan Visit 1 completed and paid.", "Front Desk", VISIT_DATE_TX_PLAN),
    historyRow("history-next", "appointment.booked", "Next Tx Plan visit booked so treatment continues.", "Front Desk", "2026-05-25T11:20:00.000Z"),
  ],
};

function flattenFindingCatalog(categories = []) {
  const rows = [];
  for (const category of categories) {
    rows.push({
      code: category.code,
      name: category.chartLabel || category.label,
      color_hex: category.colorHex || category.color_hex,
      is_pulp_involvement: false,
    });
    for (const level of category.severityLevels || []) {
      rows.push({
        code: level.code,
        name: level.chartLabel || level.label,
        color_hex: level.colorHex || level.color_hex || category.colorHex,
        is_pulp_involvement: !!level.isPulpInvolvement,
      });
    }
  }
  return rows;
}

function buildTxPlanItems() {
  const rct = DYNAMIC_CHARTING_TX_PLAN_DEMO_PROCEDURES.find((p) => p.id === "demo-tx-rct-46");
  const zirconia = DYNAMIC_CHARTING_TX_PLAN_DEMO_PROCEDURES.find((p) => p.id === "demo-tx-zirconia-46");
  return [
    planItem(rct, "46", null, 1, {
      status: "In Progress",
      fee_centavos: 850000,
      procedure_visits: [
        visit("tx-rct-46-v1", "doctor-visit-demo-tx-rct-46-1", "Pulp removal, clean and shape, temp filling", 90, 1, "completed"),
        visit("tx-rct-46-v2", "doctor-visit-demo-tx-rct-46-2", "Confirm clear, root fill, seal", 75, 2, "scheduled"),
      ],
    }),
    planItem(zirconia, "46", null, 3, {
      fee_centavos: 1800000,
      procedure_visits: [
        visit("tx-zirconia-46-v1", "doctor-visit-demo-tx-zirconia-46-prep", "Zirconia prep, scan, shade, temp crown", 90, 3, "planned"),
        visit("tx-zirconia-46-v2", "doctor-visit-demo-tx-zirconia-46-try-in", "Zirconia try-in, fit, contacts, occlusion, shade", 30, 4, "planned"),
        visit("tx-zirconia-46-v3", "doctor-visit-demo-tx-zirconia-46-cement", "Zirconia crown cementation", 45, 5, "planned"),
      ],
    }),
  ].filter(Boolean);
}

function planItem(proc, tooth, surface, phaseNumber, overrides = {}) {
  if (!proc) return null;
  const id = `${proc.id}-${tooth || "whole-mouth"}`;
  const fee = overrides.fee_centavos ?? proc.default_fee_centavos ?? 0;
  return {
    id,
    tutorial_id: getTutorialAnchor(proc, tooth),
    procedure_id: proc.id,
    procedure_code: proc.code,
    procedure_name: proc.name,
    display_label: proc.name,
    procedure_category: proc.category,
    category: proc.category,
    color: categoryColor(proc.category),
    color_hex: categoryColor(proc.category),
    fee_centavos: fee,
    committed_fee_centavos: fee,
    discount_centavos: 0,
    discount_pct: null,
    discount_amount_centavos: null,
    unit_count: 1,
    qty: 1,
    tooth_number: tooth || null,
    surface: surface || null,
    status: overrides.status || "Accepted",
    phase_number: phaseNumber,
    procedure_visits: overrides.procedure_visits || [
      visit(`${id}-visit-1`, getVisitAnchor(proc, tooth), defaultVisitLabel(proc), 45, phaseNumber, "planned"),
    ],
  };
}

function visit(id, tutorialId, templateLabel, chairMin, phaseNumber, status) {
  return {
    id,
    tutorial_id: tutorialId,
    treatment_plan_item_id: id.split("-visit-")[0],
    sequence: 1,
    template_label: templateLabel,
    chair_min: chairMin,
    lab_gap_days: null,
    phase_number: phaseNumber,
    status,
  };
}

function defaultVisitLabel(proc) {
  return proc?.name || "Planned procedure";
}

function getTutorialAnchor(proc, tooth) {
  if (proc?.id === "demo-tx-rct-46") return "doctor-item-demo-tx-rct-46";
  if (proc?.id === "demo-tx-zirconia-46") return "doctor-item-demo-tx-zirconia-46";
  return undefined;
}

function getVisitAnchor(proc, tooth) {
  return undefined;
}

function categoryColor(category) {
  if (category === "Endodontic") return "#7c3aed";
  if (category === "Oral Surgery") return "#991b1b";
  if (category === "Preventive") return "#0f766e";
  if (category === "Restorative") return "#1d4ed8";
  return "#64748b";
}

function completedWork(name, tooth_numbers, surface_codes, fee_centavos) {
  return {
    procedure_name: name,
    tooth_numbers,
    surface_codes,
    fee_centavos,
    completed_at: VISIT_DATE_INITIAL,
    performed_by_name: "Dr. Levi Jordan",
  };
}

function charge(id, procedureName, amount) {
  return {
    id,
    event_type: "charge",
    procedure_name: procedureName,
    amount_centavos: amount,
    event_date: VISIT_DATE_INITIAL,
  };
}

function payment(id, method, amount, reference) {
  return {
    id,
    event_type: "payment",
    payment_method: method,
    payment_status: "Confirmed",
    amount_centavos: amount,
    reference_number: reference,
    event_date: VISIT_DATE_INITIAL,
    document_number: id === "visit1-pay" ? 282 : 284,
  };
}

function historyRow(id, action_name, description, user_name, occurred_at) {
  return { id, action_name, description, user_name, occurred_at };
}

function medicalHistoryData() {
  return {
    allergies: {
      none: true,
      drug: { yes: false, specify: "" },
      latex: false,
      food: { yes: false, specify: "" },
      anesthetic: { yes: false, specify: "" },
      other: "",
    },
    meds: {
      none: false,
      blood_thinners: { yes: false, specify: "" },
      bisphosphonates: { yes: false, specify: "" },
      steroids_long_term: { yes: false, specify: "" },
      chemo_radiation: { yes: false, specify: "" },
      other: "",
    },
    medications: "Amlodipine 5mg once daily",
    cardiac: {
      heart_attack: { yes: false, when: "" },
      stroke: { yes: false, when: "" },
      pacemaker: false,
      artificial_valve: false,
      joint_replacement: { yes: false, specify: "" },
      rheumatic_fever: false,
      premed_required: false,
      premed_advised_by: "",
    },
    conditions: {
      hypertension: true,
      diabetes: false,
      heart_disease: false,
      asthma: false,
      bleeding_disorder: false,
      other: "Blood pressure controlled. Check chairside BP before invasive work.",
    },
    surgeries: "Appendectomy, 2015",
    pregnancy: { applicable: false, pregnant: false, weeks: "" },
    special_care: {
      developmental: false,
      cognitive: false,
      mobility: false,
      caregiver_consent: false,
      caregiver_name: "",
      communication: "",
      notes: "",
    },
    smoking: "former",
    alcohol: "occasional",
    last_physician_visit: "2026-03-10",
    bleeding_notes: "No unusual bleeding reported.",
    general_notes: "Dental anxiety mild. Explain each step before anesthesia.",
  };
}
